import type { RemoteRequest } from '@didto/domain';
import { REMOTE_POLL_MS, formatMinor, secondsUntil } from '@didto/domain';
import {
  Badge,
  Button,
  EmptyState,
  Field,
  Input,
  Loading,
  Notice,
  PageHeader,
  SectionHead,
  Surface,
} from '@didto/ui';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useOwnerReview } from '@/app/ownerReview';
import { useSession } from '@/app/session';
import { useToast } from '@/app/toast';
import { fetchRemoteApprovals } from '@/lib/api';
import { useOnline } from '@/lib/network';
import { ACTION_TITLE } from '@/lib/review';
import { approvalSummaryLines } from '../pos/approvals';
import { ReadError } from '../shifts/reviewItems';
import '../shifts/shifts.css';

export const remoteApprovalKeys = { all: ['remote-approvals'] as const };

const STATE_TEXT: Record<string, string> = {
  granted: 'Approved',
  used: 'Approved and used',
  grant_expired: 'Approved, not used in time',
  declined: 'Declined',
  withdrawn: 'Taken back by the tablet',
  expired: 'Expired without an answer',
};

/**
 * The owner's phone (ADR-033): what a tablet asked the owner to approve, approved or declined here. The tablet waits
 * and continues on its own once approved. Approving takes the owner's account with an authenticator verified and a
 * fresh authentication; the gate asks for the code when needed.
 */
export function ApprovalsPage() {
  const s = useSession();
  const enrolled = s.me?.mfa_enrolled === true;
  const q = useQuery({
    queryKey: remoteApprovalKeys.all,
    enabled: enrolled,
    queryFn: fetchRemoteApprovals,
    refetchInterval: REMOTE_POLL_MS.phone,
  });
  const waiting = q.data?.waiting ?? [];
  return (
    <div className="d-page" data-testid="approvals-page">
      <PageHeader
        eyebrow="Owner"
        title="Approvals"
        subtitle="What a tablet asked you to approve. The tablet continues on its own once you approve."
      />
      {!enrolled ? (
        <Notice
          tone="warning"
          testId="approvals-mfa-setup"
          action={
            <Link to="/settings/security" className="d-btn d-btn--sm">
              Set up an authenticator
            </Link>
          }
        >
          Approving from your phone needs an authenticator: a password alone never approves from
          afar.
        </Notice>
      ) : (
        <>
          <ReadError error={q.error} hasData={!!q.data} onRetry={() => void q.refetch()} />
          {q.isLoading && <Loading label="Loading what waits for you" rows={2} />}
          {q.data && waiting.length === 0 && (
            <EmptyState title="Nothing waits for you." testId="approvals-empty" />
          )}
          {waiting.length > 0 && (
            <div className="d-list">
              {waiting.map((r) => (
                <WaitingCard key={r.request_id} r={r} />
              ))}
            </div>
          )}
          {q.data && q.data.recent.length > 0 && (
            <section className="rv-section" aria-label="Decided">
              <SectionHead title="Last 24 hours" />
              <div className="d-list" data-testid="approvals-recent">
                {q.data.recent.map((r) => (
                  <div
                    key={r.request_id}
                    className="d-row rv-row--compact"
                    data-testid={`approval-recent-${r.request_id}`}
                  >
                    <div className="d-row__main">
                      <div className="d-row__title">
                        {ACTION_TITLE[r.action] ?? r.action}
                        {r.order_number ? ` · order ${r.order_number}` : ''}
                      </div>
                      <div className="d-row__meta">
                        {r.requested_by.name} · {r.terminal.name} · {r.location.name}
                      </div>
                    </div>
                    <div className="rv-side">
                      <Badge
                        tone={
                          r.state === 'declined'
                            ? 'danger'
                            : r.state === 'granted' || r.state === 'used'
                              ? 'success'
                              : 'neutral'
                        }
                        dot
                      >
                        <span data-testid="approval-recent-state">
                          {STATE_TEXT[r.state] ?? r.state}
                        </span>
                      </Badge>
                    </div>
                  </div>
                ))}
              </div>
            </section>
          )}
        </>
      )}
    </div>
  );
}

function WaitingCard({ r }: { r: RemoteRequest }) {
  const { run } = useOwnerReview();
  const qc = useQueryClient();
  const online = useOnline();
  const { toast } = useToast();
  const [busy, setBusy] = useState<'approve' | 'decline' | null>(null);
  const [declining, setDeclining] = useState(false);
  const [note, setNote] = useState('');
  const [, tick] = useState(0);
  useEffect(() => {
    const t = setInterval(() => tick((n) => n + 1), 1000);
    return () => clearInterval(t);
  }, []);
  const left = secondsUntil(r.expires_at);
  const currency = r.summary.currency ?? 'PHP';

  const approve = async () => {
    setBusy('approve');
    const res = await run('approval.grant_remote', { request_id: r.request_id });
    setBusy(null);
    await qc.invalidateQueries({ queryKey: remoteApprovalKeys.all });
    if (res) toast('Approved: the tablet continues', 'success');
  };
  const decline = async () => {
    setBusy('decline');
    const res = await run('approval.decline_remote', {
      request_id: r.request_id,
      ...(note.trim() ? { note: note.trim() } : {}),
    });
    setBusy(null);
    await qc.invalidateQueries({ queryKey: remoteApprovalKeys.all });
    if (res) toast('Declined: the tablet is told');
  };

  return (
    <Surface>
      <div className="d-stack" data-testid={`approval-waiting-${r.request_id}`}>
        <div>
          <div className="rv-head">
            <h2 className="d-row__title rv-card__title">{ACTION_TITLE[r.action] ?? r.action}</h2>
            {r.amount_minor != null && (
              <span className="rv-money rv-card__title" data-testid="approval-amount">
                {formatMinor(r.amount_minor, currency)}
              </span>
            )}
          </div>
          <div className="d-row__meta">
            Asked by {r.requested_by.name} on {r.terminal.name} · {r.location.name}
            {left > 0 ? ` · ${Math.ceil(left / 60)} min left` : ' · expiring'}
          </div>
        </div>
        <ul className="rv-summary" data-testid="approval-summary">
          {approvalSummaryLines(r.summary).map((line) => (
            <li key={line}>{line}</li>
          ))}
        </ul>
        {r.own_request ? (
          <p className="d-muted">You asked for this yourself: someone else approves it.</p>
        ) : declining ? (
          <>
            <Field label="Note for the tablet (optional)">
              {(id) => (
                <Input
                  id={id}
                  value={note}
                  maxLength={200}
                  onChange={(e) => setNote(e.target.value)}
                  data-testid="remote-decline-note"
                />
              )}
            </Field>
            <div className="rv-actions">
              <Button
                variant="danger"
                size="lg"
                busy={busy === 'decline'}
                disabled={!online}
                onClick={() => void decline()}
                data-testid="remote-decline-confirm"
              >
                Decline
              </Button>
              <Button variant="quiet" onClick={() => setDeclining(false)}>
                Back
              </Button>
            </div>
          </>
        ) : (
          <div className="rv-actions">
            <Button
              variant="primary"
              size="lg"
              busy={busy === 'approve'}
              disabled={!online || busy !== null}
              onClick={() => void approve()}
              data-testid="remote-approve"
            >
              Approve
            </Button>
            <Button
              variant="ghost"
              disabled={!online || busy !== null}
              onClick={() => setDeclining(true)}
              data-testid="remote-decline"
            >
              Decline
            </Button>
          </div>
        )}
      </div>
    </Surface>
  );
}
